import { computed, nextTick, ref, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useRecoverableWorkout } from '@/api/workout/queries/use-recoverable-workout';
import { useFinishWorkoutFromCheckpoint } from '@/api/workout/mutations/use-finish-workout-from-checkpoint';
import { useEarlyExitWorkout } from '@/api/workout/mutations/use-early-exit-workout';
import { useAbandonWorkout } from '@/api/workout/mutations/use-abandon-workout';
import { useRouterInTab } from '@/hooks/use-router-in-tab';
import { RouteName } from '@/router';
import { resolveRecoveryFinishDecision } from '@/views/workout-player/utils/recovery-finish-decision';
import type { WorkoutExitReason } from '@/api/workout/types/exit-reason';

/**
 * Session-scoped dismissal: hide for this app session; ask again on next cold launch.
 * Does not mutate the workout event.
 */
let dismissedThisSession = false;

/** Test helper — reset session dismissal between unit tests. */
export function resetWorkoutRecoverySessionDismissal() {
  dismissedThisSession = false;
}

/** True when the authenticated app shell (Homepage / any main tab) is active. */
export function isAppShellHomePath(path: string): boolean {
  const appPrefix = `/${RouteName.App}/`;
  if (!path.startsWith(appPrefix)) return false;
  // Suppress while already on the player / finish / feedback for any workout.
  if (path.includes(`/${RouteName.WorkoutPlayer}/`)) return false;
  return true;
}

/** Resume navigation: path + query (query must not be embedded in the path string). */
export function buildResumePlayerLocation(workoutEventId: string) {
  return {
    path: `/${RouteName.WorkoutPlayer}/${workoutEventId}`,
    query: { resume: '1' },
  };
}

export function useWorkoutRecovery(userReady: Ref<boolean>) {
  const route = useRoute();
  const routerInTab = useRouterInTab();
  const shellReady = ref(false);

  const onAppShellHome = computed(() => isAppShellHomePath(route.path));
  const enabled = computed(() => userReady.value && onAppShellHome.value && !dismissedThisSession && shellReady.value);

  const { data: recoverable, isFetching, refetch } = useRecoverableWorkout(enabled);
  const { finishWorkoutFromCheckpoint, loading: finishing } = useFinishWorkoutFromCheckpoint();
  const { earlyExitWorkout, loading: earlyExiting } = useEarlyExitWorkout(undefined);
  const { abandonWorkout, loading: abandoning } = useAbandonWorkout();

  const open = ref(false);
  const earlyExitOpen = ref(false);
  const workout = computed(() => recoverable.value ?? null);

  // Homepage / app shell must paint first, then allow the overlay to open.
  watch(
    [userReady, onAppShellHome],
    async ([ready, onHome]) => {
      if (!ready || !onHome || dismissedThisSession) {
        shellReady.value = false;
        open.value = false;
        return;
      }
      shellReady.value = false;
      await nextTick();
      // One frame after the shell route is active so Feed (or current tab) is visible.
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => resolve());
      });
      if (userReady.value && onAppShellHome.value && !dismissedThisSession) {
        shellReady.value = true;
      }
    },
    { immediate: true }
  );

  watch(
    [recoverable, enabled],
    ([event, isEnabled]) => {
      if (!isEnabled) {
        open.value = false;
        earlyExitOpen.value = false;
        return;
      }
      if (earlyExitOpen.value) return;
      open.value = !!event;
    },
    { immediate: true }
  );

  const dismissForSession = () => {
    dismissedThisSession = true;
    open.value = false;
    earlyExitOpen.value = false;
    shellReady.value = false;
  };

  const onResume = () => {
    const event = workout.value;
    if (!event) return;
    open.value = false;
    dismissedThisSession = true;
    // Pass query separately — string "?resume=1" is incorrectly embedded in path by useRouterInTab.
    const location = buildResumePlayerLocation(event.workoutEventId);
    routerInTab.push(location.path, location.query);
  };

  const onFinishNow = async () => {
    const event = workout.value;
    if (!event) return;

    const decision = resolveRecoveryFinishDecision(event);
    if (decision.action === 'insufficient_state') {
      // Do not guess a completion threshold without planned duration + progress.
      return;
    }
    if (decision.action === 'early_exit') {
      open.value = false;
      earlyExitOpen.value = true;
      return;
    }

    try {
      await finishWorkoutFromCheckpoint(event.workoutEventId);
      open.value = false;
      earlyExitOpen.value = false;
      dismissedThisSession = true;
      routerInTab.push(`/${RouteName.WorkoutPlayer}/${event.workoutEventId}/finish`);
    } catch {
      // toast from mutation
    }
  };

  const onEarlyExitClose = () => {
    earlyExitOpen.value = false;
    if (!dismissedThisSession && workout.value) {
      open.value = true;
    }
  };

  const onEarlyExitContinue = () => {
    earlyExitOpen.value = false;
    onResume();
  };

  // Set synchronously; earlyExiting only flips after the mutation observer notifies.
  let earlyExitInFlight = false;

  const onEarlyExitSubmit = async (payload: { exitReasons: WorkoutExitReason[]; exitReasonText?: string }) => {
    const event = workout.value;
    if (!event || earlyExitInFlight) return;
    earlyExitInFlight = true;
    try {
      await earlyExitWorkout({
        workoutEventId: event.workoutEventId,
        completePercentage: event.completePercentage,
        totalBackgroundDuration: event.totalBackgroundDuration,
        totalPauseDuration: event.totalPauseDuration,
        exerciseActiveDuration: event.exerciseActiveDuration ?? undefined,
        currentExerciseIndex: event.currentExerciseIndex,
        exitReasons: payload.exitReasons,
        exitReasonText: payload.exitReasonText,
        fromCheckpoint: true,
      });
      earlyExitOpen.value = false;
      open.value = false;
      dismissedThisSession = true;
    } catch {
      // toast from mutation
    } finally {
      earlyExitInFlight = false;
    }
  };

  const onEarlyExitQuit = () => onEarlyExitSubmit({ exitReasons: [] });

  const onDiscard = async () => {
    const event = workout.value;
    if (!event) return;
    try {
      await abandonWorkout(event.workoutEventId);
      open.value = false;
      dismissedThisSession = true;
    } catch {
      // toast from mutation
    }
  };

  return {
    open,
    earlyExitOpen,
    workout,
    isFetching,
    busy: computed(() => finishing.value || abandoning.value || earlyExiting.value),
    earlyExitSubmitting: earlyExiting,
    onResume,
    onFinishNow,
    onDiscard,
    onEarlyExitClose,
    onEarlyExitContinue,
    onEarlyExitSubmit,
    onEarlyExitQuit,
    dismissForSession,
    refetch,
  };
}
